// DataTableHeader.jsx
// Shared header treatment for the app's data tables so they all look identical:
// a muted header band with uppercase, letter-spaced micro-labels, plus a
// monochrome sortable-header cell (faint bidirectional arrow at rest, dark
// chevron when active). Spread `dataTableHeadSx` into a <Table sx> and use
// <SortableHeadCell> for clickable/sortable columns.
import PropTypes from "prop-types";
import { TableCell, TableSortLabel } from "@mui/material";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import UnfoldMoreRoundedIcon from "@mui/icons-material/UnfoldMoreRounded";
import {
  SURFACE_MUTED,
  HAIRLINE,
  HAIRLINE_STRONG,
  INK,
  INK_SECONDARY,
} from "../theme/tokens";

// Spread into <Table sx={{ ...dataTableHeadSx }}> — styles every header cell.
export const dataTableHeadSx = {
  "& thead th": {
    bgcolor: SURFACE_MUTED,
    borderBottom: `1px solid ${HAIRLINE_STRONG}`,
    color: INK_SECONDARY,
    fontSize: 11,
    fontWeight: 600,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    whiteSpace: "nowrap",
    py: 1.25,
  },
};

// A sortable header cell: the whole cell is clickable, monochrome so the column
// never recolors, with a bidirectional arrow at rest and a thin chevron that
// darkens / flips direction once the column drives the sort.
export function SortableHeadCell({
  label,
  active = false,
  direction = "asc",
  onClick,
  width,
  align,
  sx,
}) {
  return (
    <TableCell
      align={align}
      sortDirection={active ? direction : false}
      onClick={onClick}
      sx={{
        width,
        cursor: "pointer",
        userSelect: "none",
        transition: "background-color 0.15s ease",
        "&:hover": { bgcolor: HAIRLINE },
        ...sx,
      }}
    >
      <TableSortLabel
        active={active}
        direction={active ? direction : "asc"}
        hideSortIcon={false}
        IconComponent={
          active ? KeyboardArrowDownRoundedIcon : UnfoldMoreRoundedIcon
        }
        sx={{
          color: active ? INK : INK_SECONDARY,
          transition: "color 0.15s ease",
          "&:hover, &.Mui-active, &.Mui-active:hover": { color: INK },
          "& .MuiTableSortLabel-icon": {
            fontSize: 18,
            marginLeft: "2px",
            color: "inherit",
            opacity: active ? 1 : 0.45,
            transition: "opacity 0.15s ease, transform 0.2s ease",
          },
          "&:hover .MuiTableSortLabel-icon": { opacity: active ? 1 : 0.7 },
        }}
      >
        {label}
      </TableSortLabel>
    </TableCell>
  );
}

SortableHeadCell.propTypes = {
  label: PropTypes.node,
  active: PropTypes.bool,
  direction: PropTypes.oneOf(["asc", "desc"]),
  onClick: PropTypes.func,
  width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  align: PropTypes.string,
  sx: PropTypes.object,
};
